<template>
	<div class="rich-text-container">
		<!-- 加载状态 -->
		<div v-if="loading" class="loading-state">
			<div class="spinner"></div>
			<p>内容加载中...</p>
		</div>

		<!-- 错误状态 -->
		<div v-else-if="error" class="error-state">
			<i class="icon-error"></i>
			<p>{{ error }}</p>
			<button @click="retry" class="retry-btn">重新加载</button>
		</div>

		<!-- 空状态 -->
		<div v-else-if="!content" class="empty-state">
			<i class="icon-empty"></i>
			<p>暂无内容</p>
		</div>

		<!-- 内容展示 -->
		<div v-else class="content-wrapper">
			<div v-html="content" class="rich-content"></div>
		</div>
	</div>
</template>

<script>
	export default {
		name: 'RichTextDisplay',
		data() {
			return {
				content: '',
				loading: false,
				error: '',
				currentId: null
			}
		},
		watch: {
			'$route.query.id': {
				handler(newId) {
					if (newId && newId !== this.currentId) {
						this.getData(newId);
					}
				},
				immediate: true
			}
		},
		methods: {
			async getData(id) {
				if (!id) {
					this.content = '';
					return;
				}

				this.loading = true;
				this.error = '';
				this.currentId = id;

				try {
					// 添加请求超时处理
					const response = await Promise.race([
						this.$get('app-agreement/' + id),
						new Promise((_, reject) =>
							setTimeout(() => reject(new Error('请求超时')), 10000)
						)
					]);

					const data = response.data;

					// 安全处理HTML内容
					if (data.content) {
						this.content = this.sanitizeHTML(data.content);
					} else {
						this.content = '';
					}
				} catch (err) {
					console.error('获取内容失败:', err);
					this.error = err.message || '加载失败，请稍后重试';
					this.content = '';
				} finally {
					this.loading = false;
				}
			},

			retry() {
				if (this.currentId) {
					this.getData(this.currentId);
				} else if (this.$route.query.id) {
					this.getData(this.$route.query.id);
				}
			},

			// 简单的HTML清理函数（生产环境建议使用DOMPurify等库）
			sanitizeHTML(html) {
				// 移除危险的标签和属性
				return html
					.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
					.replace(/on\w+="[^"]*"/g, '')
					.replace(/on\w+='[^']*'/g, '')
					.replace(/javascript:/gi, '');
			}
		},

		beforeDestroy() {
			// 清理工作
			this.content = '';
			this.error = '';
		}
	}
</script>

<style lang="scss" scoped>
	.rich-text-container {
		padding: 40px;
		background-color: #fff;
		min-height: 100vh;
		width: 100%;
		display: flex;
		justify-content: center;
		align-items: flex-start;

		.loading-state,
		.error-state,
		.empty-state {
			display: flex;
			flex-direction: column;
			align-items: center;
			justify-content: center;
			padding: 60px 20px;
			text-align: center;

			p {
				margin-top: 16px;
				color: #666;
				font-size: 16px;
			}
		}

		.spinner {
			width: 40px;
			height: 40px;
			border: 4px solid #f3f3f3;
			border-top: 4px solid #409eff;
			border-radius: 50%;
			animation: spin 1s linear infinite;
		}

		@keyframes spin {
			0% {
				transform: rotate(0deg);
			}

			100% {
				transform: rotate(360deg);
			}
		}

		.error-state,
		.empty-state {
			i {
				font-size: 48px;
				color: #c0c4cc;
				margin-bottom: 16px;
			}
		}

		.retry-btn {
			margin-top: 16px;
			padding: 8px 16px;
			background-color: #409eff;
			color: white;
			border: none;
			border-radius: 4px;
			cursor: pointer;
			transition: background-color 0.3s;

			&:hover {
				background-color: #66b1ff;
			}
		}

		.content-wrapper {
			max-width: 800px;
			width: 100%;

			.rich-content {
				line-height: 1.6;

				// 富文本内容样式重置和优化
				::v-deep {

					h1,
					h2,
					h3,
					h4,
					h5,
					h6 {
						margin: 1.2em 0 0.6em;
						color: #303133;
					}

					p {
						margin: 0.8em 0;
						color: #606266;
					}

					img {
						max-width: 100%;
						height: auto;
						border-radius: 4px;
					}

					table {
						width: 100%;
						border-collapse: collapse;
						margin: 1em 0;

						th,
						td {
							border: 1px solid #eaeaea;
							padding: 8px 12px;
							text-align: left;
						}

						th {
							background-color: #f5f7fa;
							font-weight: 600;
						}
					}

					blockquote {
						border-left: 4px solid #409eff;
						background-color: #f0f7ff;
						margin: 1em 0;
						padding: 12px 16px;
						color: #606266;
					}

					ul,
					ol {
						padding-left: 2em;
						margin: 1em 0;
					}

					li {
						margin: 0.4em 0;
					}

					a {
						color: #409eff;
						text-decoration: none;

						&:hover {
							text-decoration: underline;
						}
					}

					code {
						background-color: #f5f7fa;
						padding: 2px 6px;
						border-radius: 3px;
						font-family: 'Courier New', monospace;
					}

					pre {
						background-color: #f5f7fa;
						padding: 16px;
						border-radius: 4px;
						overflow-x: auto;
						margin: 1em 0;

						code {
							background: none;
							padding: 0;
						}
					}
				}
			}
		}
	}

	// 响应式设计
	@media (max-width: 768px) {
		.rich-text-container {
			padding: 20px 16px;

			.content-wrapper {
				max-width: 100%;
			}
		}
	}
</style>